import {css} from '@mui/material';
import {DlMuiDynamicIcon} from '@wallaby-ui/core';
import React from 'react';

import {AlertTitleTooltip} from './alert/AlertTitleTooltip';

/** The tint of the button: teal for Solve / Restore, amber for Snooze, red for Deactivate, grey otherwise. */
export type LightPanelActionTone = 'neutral' | 'positive' | 'snooze' | 'negative';

type Props = {
  iconName: string;
  viewBox: string;
  /** The translated text of the app's dark tooltip above the button and of its aria-label; no tooltip when empty. */
  title?: string;
  tone?: LightPanelActionTone;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  /** The button's menu or confirmation is open: the snooze button keeps its open tint. */
  open?: boolean;
  /** Hides the tooltip, e.g. while the button's own menu is open. */
  hideTitle?: boolean;
  dataName?: string;
  /** `data-action` of the button, what tests and the open tint select by. */
  action?: string;
};

/* `.iconbtn` of the alerts design: 30px squares with a 1px border, tinted per tone */
const buttonCss = css`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid var(--dlAlertActionBorder);
  background: #fff;
  color: var(--dlThemeGray-600);
  cursor: pointer;
  &:hover {
    background: var(--dlThemeGray-50);
  }
  &[data-tone='positive'] {
    color: var(--dlTheme-500);
    border-color: var(--dlAlertPositiveActionBorder);
  }
  &[data-tone='snooze'] {
    color: var(--dlAlertSnoozeActionColor);
    border-color: var(--dlAlertSnoozeActionBorder);
  }
  &[data-tone='snooze'][data-open='true'] {
    background: var(--dlAlertSnoozeActionOpenBackground);
    border-color: var(--dlPastelOrangeColor);
  }
  &[data-tone='negative'] {
    color: var(--dlPanelNegativeActionColor);
    border-color: var(--dlPanelNegativeActionBorder);
  }
  &[data-tone='negative']:hover,
  &[data-tone='negative'][data-open='true'] {
    background: var(--dlPanelNegativeActionHoverBackground);
  }
  &[data-disabled='true'] {
    opacity: 0.5;
    pointer-events: none;
  }
`;
const iconCss = css`
  font-size: 16px;
`;

/**
 * The right-edge row action of the right-panel tabs (Alerts panel episodes, risk factors): one icon in a tinted square
 * with the app's dark tooltip. It is a div with the button role: DlMuiDynamicIcon renders a MUI IconButton, and a button
 * inside a button is invalid DOM.
 */
export const LightPanelActionButton: React.FC<Props> = (props) => {
  const {iconName, viewBox, title, tone = 'neutral', onClick, disabled, open, hideTitle, dataName, action} = props;
  return (
    <AlertTitleTooltip title={hideTitle ? undefined : title}>
      <div
        role='button'
        tabIndex={disabled ? -1 : 0}
        css={buttonCss}
        aria-label={title}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            (event.currentTarget as HTMLElement).click();
          }
        }}
        data-tone={tone}
        data-action={action}
        data-open={Boolean(open)}
        data-disabled={Boolean(disabled)}
        data-name={dataName}
        onClick={onClick}
      >
        {/* the inner IconButton is only the icon: the div is the one tab stop and the one control a screen reader names */}
        <DlMuiDynamicIcon
          iconName={iconName}
          useFixedViewBox={viewBox}
          tabIndex={-1}
          aria-hidden
          noButtonStyles
          style={{color: 'inherit', padding: 0, margin: 0, pointerEvents: 'none'}}
          svgIconInputCss={iconCss}
        />
      </div>
    </AlertTitleTooltip>
  );
};

LightPanelActionButton.displayName = 'LightPanelActionButton';
